//
// File: CognitoUser.ts
// Description: Cognito implmentation of the User class
// Handy reference for Cognito functions: https://unpkg.com/browse/amazon-cognito-identity-js@6.3.7/index.d.ts
// Copyright: Broadlume 2024
//

/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */

import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';

import { User } from './User';
import { UserAttributes } from './UserAttributes';
import { UserAccountAPI } from '../api/UserAccount';
import { AuthChallengeName } from './AuthChallengeName';
import { AuthChallengeCallback } from './AuthChallengeCallback';
import { Buffer } from 'buffer';
import { ApiRoutes, getFullApiRoute } from '../api/ApiRoutes';

const kTokenRefreshIntervalMinutes = 30.0; // 30 minutes

/**
 * Synthesised error messages surfaced by refreshSession() when the session has
 * no refresh token or no Cognito user to refresh against. Exported so callers
 * (e.g. requestQueue) can recognise these as auth failures without string-matching.
 */
export const NO_REFRESH_TOKEN_ERROR = 'No refresh token available';
export const NO_COGNITO_USER_ERROR = 'No cognito user available';

/**
 * CognitoUser class, representing a user in the Cognito user pool
 * Implements the User interface
 */
export class CognitoUser extends User {
  /*
   * Pool data provided by the environment
   */
  private _poolData = {
    /**
     * Cognito user pool id
     */
    UserPoolId: process.env['NX_PUBLIC_COGNITO_USER_POOL']!,
    /**
     * The client id for the shell host app
     */
    ClientId: process.env['NX_PUBLIC_COGNITO_SHELL_APP_CLIENT_ID']!,
    //Storage: new AmazonCognitoIdentity.CookieStorage({domain: ".yourdomain.com"})
  };

  /**
   * Cognito user pool object intansiated with the pool data
   */
  private _userPool = new AmazonCognitoIdentity.CognitoUserPool(this._poolData);

  /**
   * Cognito user object
   */
  private _cognitoUser: AmazonCognitoIdentity.CognitoUser | null = null;

  /**
   * Cognito user session object
   */
  private _session: AmazonCognitoIdentity.CognitoUserSession | null = null;

  /**
   * Timer used to invoke refresh session
   */
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Current Auth Challenge Name if any, otherwise null
   */
  private _authChallengeName: AuthChallengeName | null = null;

  /**
   * Callback interface for Auth Challenges, used to invoke UI navigation etc
   */
  private _authChallengeCallback: AuthChallengeCallback | null = null;

  /**
   * In-flight refresh promise, shared across concurrent callers so parallel
   * `refreshSession()` calls collapse to a single Cognito InitiateAuth request.
   */
  private _refreshPromise: Promise<void> | null = null;

  /**
   * Local copy of Cognito user identity attributes
   */
  private _localAttributes: UserAttributes = {
    id: '',
    username: '',
    email: '',
    name: '',
    surname: '',
    phone: '',
    address: '',
    dob: '',
    picture: '',
    locale: '',
    zoneInfo: '',
    userId: '',
    adminLevel: 0,
    tenant_id: '',
    is_internal_user: 'false',
    emailVerified: false,
    phoneVerified: false,
    roles: [],
    is_super_admin: false,
  };

  /**
   * Set the auth challenge callback
   * @param callback - The callback to set
   */
  set authChallengeCallback(callback: AuthChallengeCallback) {
    this._authChallengeCallback = callback;
  }

  /**
   * Get the cognito user object
   * @returns The cognito user object
   */
  get cognitoUser() {
    return this._cognitoUser;
  }
  /**
   * Set the cognito user object
   * @param cognitoUser - A cognito user object
   */
  set cognitoUser(cognitoUser: AmazonCognitoIdentity.CognitoUser | null) {
    this._cognitoUser = cognitoUser;
  }

  /**
   * Get the cognito user session object
   * @returns The cognito user session object
   */
  get congnitoSession() {
    return this._session;
  }
  /**
   * Set the cognito user session object
   * @param session - A cognito user session object
   */
  set congnitoSession(session: AmazonCognitoIdentity.CognitoUserSession | null) {
    this._session = session;
  }

  /**
   * Resume the current session if one exists. Sends a getSession request to Cognito
   * if the user is authenticated the local session info is updated
   * @returns A promise that resolves when the session is resumed
   */
  resumeSession(): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = this._userPool.getCurrentUser();
      if (!cognitoUser) {
        return resolve();
      }
      cognitoUser.getSession((err: unknown, result: AmazonCognitoIdentity.CognitoUserSession) => {
        if (result) {
          this.cognitoUser = cognitoUser;
          this.congnitoSession = result;

          // Bypass cache on session resume: attributes may have changed
          // server-side (admin action, another device) since the cache was
          // last written.
          this.initaliseUserAttributes(true)
            .then(() => {
              this.setRefreshTimer(kTokenRefreshIntervalMinutes);
              resolve();
            })
            .catch((error) => {
              reject(error);
            });
        } else {
          reject(err);
        }
      });
    });
  }

  /**
   * Sign into the Cognito user pool, sends an authenticateUser request to Cognito.
   * If the user is authenticated the local session info is updated
   * @param username - The username for user
   * @param password - The password for user
   * @returns A promise that resolves when the user is authenticated
   */
  signIn(username: string, password: string): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    const authenticationData = {
      Username: username,
      Password: password,
    };
    const authenticationDetails = new AmazonCognitoIdentity.AuthenticationDetails(
      authenticationData,
    );

    const userData = {
      Username: username,
      Pool: self._userPool,
      //Storage: new AmazonCognitoIdentity.CookieStorage({domain: ".yourdomain.com"})
    };

    this.cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);

    return new Promise((resolve, reject) => {
      if (!self.cognitoUser) return reject();

      self.cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (
          session: AmazonCognitoIdentity.CognitoUserSession,
          userConfirmationNecessary?: boolean,
        ) => {
          //this.cognitoUser = cognitoUser;
          this.congnitoSession = session;
          // Bypass cache on sign-in: new session, and any cached userData
          // for this username could be stale from a previous visit.
          this.initaliseUserAttributes(true).then(() => {
            this.setRefreshTimer(kTokenRefreshIntervalMinutes);
            resolve();
          });
        },
        onFailure: (err: any) => {
          console.log('Error logging in!');
          self.cognitoUser = null;
          reject(err);
        },
        mfaSetup: (
          challengeName: AmazonCognitoIdentity.ChallengeName,
          challengeParameters: any,
        ) => {
          self._authChallengeName = AuthChallengeName.MFA_SETUP;
          //cognitoUser.associateSoftwareToken(this);
        },

        selectMFAType: (
          challengeName: AmazonCognitoIdentity.ChallengeName,
          challengeParameters: any,
        ) => {
          //const mfaType = prompt('Please select the MFA method.', ''); // valid values for mfaType is "SMS_MFA", "SOFTWARE_TOKEN_MFA"
          self._authChallengeName = AuthChallengeName.SELECT_MFA_TYPE;
          //cognitoUser.sendMFASelectionAnswer(mfaType, this);
        },

        totpRequired: (
          challengeName: AmazonCognitoIdentity.ChallengeName,
          challengeParameters: any,
        ) => {
          //const challengeAnswer = prompt('Please input the TOTP code.', '');
          self._authChallengeName = AuthChallengeName.TOTP_REQUIRED;
          //cognitoUser.sendMFACode(challengeAnswer, this, 'SOFTWARE_TOKEN_MFA');
        },

        mfaRequired: (
          challengeName: AmazonCognitoIdentity.ChallengeName,
          challengeParameters: any,
        ) => {
          // MFA is required to complete user authentication.
          // Get the code from user and call
          self._authChallengeName = AuthChallengeName.MFA_REQUIRED;
          //cognitoUser.sendMFACode(mfaCode, this)
        },

        newPasswordRequired: function (userAttributes: any, requiredAttributes: any) {
          // User was signed up by an admin and must provide new
          // password and required attributes, if any, to complete
          // authentication.

          // remove any attributes what require verification
          // as these can't be directly set
          delete userAttributes.email_verified;
          delete userAttributes.email;
          delete userAttributes.phone_number_verified;
          delete userAttributes.phone_number;

          self._authChallengeName = AuthChallengeName.NEW_PASSWORD_REQUIRED;

          // if a callback is set invoke the new password flow
          if (self._authChallengeCallback) {
            self._authChallengeCallback.newPasswordRequired(userAttributes, requiredAttributes);
            resolve();
          } else {
            reject(new Error('Callback is null'));
          }
        },
      });
    });
  }

  /**
   * Sign out the current user, sends a signOut request to Cognito.
   * If the user is signed out the local session info is cleared
   * @returns A promise that resolves when the user is signed out
   */
  override signOut(): Promise<void> {
    const cognitoUser = this.cognitoUser;
    if (!cognitoUser) return Promise.resolve();
    return new Promise((resolve, reject) => {
      cognitoUser.signOut(() => {
        localStorage.clear();
        this.congnitoSession = null;
        // clear refresh timer
        if (this.refreshTimer) clearTimeout(this.refreshTimer);
        resolve();
      });
    });
  }

  /**
   * Clears any stored session info from the browser
   * this info is used when resuming a session
   * @private
   * TODO: The cognito clearCachedUser method is not exposed in the typescript definitions
   * @returns A promise that resolves when the session info is cleared
   */
  override clearStoredSessionInfo(): Promise<void> {
    const cognitoUser = this.cognitoUser;
    if (!cognitoUser) return Promise.resolve();
    return new Promise((resolve, reject) => {
      //cognitoUser.clearCachedUser(); // This isn't exposed in the typescript definitions
      resolve();
    });
  }

  /**
   * Switch the users current tenant, invokes the switch tenant api
   * to request a switch of tenant. The user must have a membership
   * to the tenant to switch to it.
   * @param tenantId - The id of the tenant to switch to
   * @returns A promise that resolves when the tenant is switched
   */
  override switchTenant(tenantId: string): Promise<void> {
    const cognitoUser = this.cognitoUser;
    const idToken = this.idToken;
    if (!cognitoUser) return Promise.resolve();
    return new Promise((resolve, reject) => {
      // add the authorization header to the request
      const headers = new Headers();
      headers.append('Content-Type', 'application/json');
      headers.append('Authorization', 'Bearer ' + idToken);

      try {
        // send the fetch request - wrapped in trackPromise to track the promise
        return fetch(getFullApiRoute(ApiRoutes.SWITCH_TENANT_V3) + '/' + tenantId, {
          method: 'POST',
          body: null,
          headers: headers as Headers,
        })
          .then((response) => {
            resolve();
          })
          .catch((err) => {
            // only rethrow if the error is not an abort error
            // this is due to react devmode mounting an unmounting the component
            // causing an abort error every time the component is first mounted
            if (err.name !== 'AbortError') throw err;
          });
      } catch (err: any) {
        return reject(err);
      }
    });
  }

  /**
   * Refresh the current session. Sends a refreshSession request to Cognito.
   * If the user is authenticated the local session info is updated
   * @returns A promise that resolves when the session is refreshed
   */
  override refreshSession(force = false): Promise<void> {
    if (this._refreshPromise) {
      if (!force) return this._refreshPromise;
      // A forced caller can't safely share an in-flight refresh: the tokens
      // it returns may have been minted before whatever backend state change
      // triggered this call (e.g. /switch_tenant mutated the user's tenant
      // server-side just before we got here). Wait for the current refresh
      // to settle, then start a fresh one.
      return this._refreshPromise
        .catch(() => {
          /* swallow — we're retrying regardless of outcome */
        })
        .then(() => this.refreshSession(true));
    }

    // Short-circuit when the current session is still valid and the caller
    // hasn't explicitly asked for a forced refresh (tenant switch, 401 retry,
    // proactive timer). `isValid()` compares the ID token `exp` against now.
    if (!force && this._session?.isValid()) {
      return Promise.resolve();
    }

    const cognitoUser = this.cognitoUser;
    if (!cognitoUser) {
      return Promise.reject(new Error(NO_COGNITO_USER_ERROR));
    }
    const refreshToken = this._session?.getRefreshToken();
    if (!refreshToken) {
      return Promise.reject(new Error(NO_REFRESH_TOKEN_ERROR));
    }
    const promise = new Promise<void>((resolve, reject) => {
      cognitoUser.refreshSession(
        refreshToken,
        (err: any, session: AmazonCognitoIdentity.CognitoUserSession) => {
          if (err) {
            console.log('Error refreshing session ' + err);
            return reject(err);
          }
          this.congnitoSession = session;
          // A missing .catch here would hang the outer Promise forever if
          // getUserData / attribute init failed — queued 401 retries would
          // then never settle and the UI would spin indefinitely.
          this.initaliseUserAttributes()
            .then(() => {
              this.setRefreshTimer(kTokenRefreshIntervalMinutes);
              resolve();
            })
            .catch(reject);
        },
      );
    });
    this._refreshPromise = promise;
    promise.finally(() => {
      if (this._refreshPromise === promise) this._refreshPromise = null;
    });
    return promise;
  }

  /**
   * Start a timer to refresh the session after a specified interval
   * @param intervalMinutes - The interval in minutes to refresh the session
   */
  setRefreshTimer(intervalMinutes: number): void {
    // Clear any existing timer
    if (this.refreshTimer) clearTimeout(this.refreshTimer);

    // Set the timer to call refreshSession after the specified interval.
    // Force a refresh: the timer's job is to proactively rotate tokens before
    // expiry, so `isValid()` will still be true when it fires.
    this.refreshTimer = setTimeout(
      () => {
        this.refreshSession(true).catch((err) => {
          console.log('Error refreshing session:', err);
        });
      },
      intervalMinutes * 60 * 1000,
    ); // Convert minutes to milliseconds
  }

  /**
   * Initialise the user attributes, fetches the user data from Cognito
   * then stores the attributes locally.
   * @param bypassCache - When true, always read attributes from Cognito and
   *                      refresh the SDK's localStorage cache. Use on session
   *                      entry-points (resumeSession, signIn) to pick up changes
   *                      made since the last visit. False is safe mid-session
   *                      (refreshSession) since attributes don't change without
   *                      a local write, and writes already invalidate the cache.
   * @returns A promise that resolves when the user attributes are initialised
   */
  initaliseUserAttributes(bypassCache = false): Promise<void> {
    return new Promise((resolve, reject) => {
      this.fetchUserData(bypassCache)
        .then((attributes) => {
          this.attributes = attributes;
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Complete the new password challenge for a user passing in the new password
   * and required attributes to complete the challenge.
   * Sends the completeNewPasswordChallenge to Cognito and updates the local
   * session and user attributes if successful.
   * @param pwd  - The new password
   * @param attrbutes - The required attributes
   * @param fullFormatPhone - Optional phone number in full format (e.g. AD-+376-22322222) for database storage
   * @returns A promise that resolves when the challenge is completed
   */
  completeNewPasswordChallenge(
    pwd: string,
    attrbutes: Record<string, string>,
    fullFormatPhone?: Record<string, string>,
  ): Promise<void> {
    const user = this.cognitoUser;
    return new Promise((resolve, reject) => {
      if (!user) return reject(new Error('Cognito user is null'));
      user.completeNewPasswordChallenge(pwd, attrbutes, {
        onSuccess: (session: AmazonCognitoIdentity.CognitoUserSession) => {
          this.congnitoSession = session;
          // Bypass cache: the challenge may have set required attributes,
          // so the SDK's localStorage copy is stale.
          this.fetchUserData(true).then((attributes) => {
            this.attributes = attributes;
            // If fullFormatPhone is provided, use it for database storage instead of Cognito's phone
            if (fullFormatPhone && fullFormatPhone['phone']) {
              this.attributes['phone'] = fullFormatPhone['phone'];
            }
            // for now lets also sync the user account details
            return UserAccountAPI.updateDetails(this).then(() => {
              resolve();
            });
          });
        },
        onFailure: (err: any) => {
          console.log('Error logging in!');
          this.cognitoUser = null;
          reject(err);
        },
      });
    });
  }

  /**
   * Request a verfication code for an attribute from Cognito.
   * Sends a getAttributeVerificationCode request to Cognito.
   * If an auth challenge callback is set it will be invoked to prompt
   * the user to enter the code.
   * @param attrbuteName - The name of the attribute to verify
   * @returns A promise that resolves when the code is requested
   */
  requestVerificationCode(attrbuteName: string): Promise<void> {
    const authCallback = this._authChallengeCallback;
    const user = this.cognitoUser;
    return new Promise((resolve, reject) => {
      if (!user) return reject(new Error('Cognito user is null'));

      user.getAttributeVerificationCode('email', {
        onSuccess: function (result) {
          resolve();
        },
        onFailure: function (err) {
          //alert(err);
          reject(err);
        },
        inputVerificationCode: function (data: string) {
          if (authCallback) {
            // invoke the callback prompting for the code
            authCallback.enterVerificationCode(attrbuteName);
          }
          resolve();
        },
      });
    });
  }

  /**
   * Verify an attribute using the code sent to the user.
   * Sends a verifyAttribute request to Cognito.
   * @param attrbuteName - The name of the attribute to verify
   * @param code - The code to verify the attribute
   * @returns A promise that resolves when the attribute is verified
   */
  veryfyAttribute(attrbuteName: string, code: string): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const user = this.cognitoUser;
    return new Promise((resolve, reject) => {
      if (!user) return reject(new Error('Cognito user is null'));

      user.verifyAttribute(attrbuteName, code, {
        onSuccess: function (result) {
          // refresh the user data to ensure the attribute is updated
          self.initaliseUserAttributes().then(() => {
            resolve();
          });
        },
        onFailure: function (err) {
          reject(err);
        },
      });
    });
  }

  /**
   * Send a forgot password request to Cognito.
   * @param username - The username to send the forgot password request to
   * @returns A promise that resolves when the forgot password request is sent
   */
  forgotPassword(username: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const userData = {
        Username: username,
        Pool: this._userPool,
      };

      const user = new AmazonCognitoIdentity.CognitoUser(userData);

      if (!user) return reject(new Error('Cognito user is null'));

      user.forgotPassword({
        onSuccess: (data: any) => {
          resolve();
        },
        onFailure: (err: any) => {
          console.log('Error', err);
          reject(err);
        },
      });
    });
  }

  /**
   * Confirms a forgot password request for a user. Sends a confirmPassword request to Cognito.
   * @param username - The username of the user that is resetting their password
   * @param password - The new password
   * @param code - The verification code sent to the user
   * @returns A promise that resolves when the password is reset
   */
  confirmForgotPassword(username: string, password: string, code: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const userData = {
        Username: username,
        Pool: this._userPool,
      };

      const user = new AmazonCognitoIdentity.CognitoUser(userData);

      if (!user) return reject(new Error('Cognito user is null'));

      user.confirmPassword(code, password, {
        onSuccess: function (success: string) {
          console.log('call result: ' + success);
          resolve();
        },
        onFailure: function (err) {
          reject(err);
        },
      });
    });
  }

  /**
   * Change the users password. Sends a changePassword request to Cognito.
   * @param oldPassword - The current password for the user
   * @param newPassword - The new password for the user
   * @returns A promise that resolves when the password is changed
   */
  changePassword(oldPassword: string, newPassword: string): Promise<void> {
    const cognitoUser = this.cognitoUser;
    if (!cognitoUser) return Promise.reject(new Error('Cognito user is null'));
    return new Promise((resolve, reject) => {
      cognitoUser.changePassword(oldPassword, newPassword, (err?: Error, result?: string) => {
        if (err) {
          console.log('Error changing password ' + err);
          reject(err);
        } else {
          resolve();
        }
      });
    });
  }

  /**
   * Get the local copy of user attributes
   * @returns The local copy of user attributes
   */
  get attributes(): UserAttributes {
    return this._localAttributes;
  }

  /*
   * Set the local copy of user attributes
   * @param attributes - The user attributes to set
   */
  set attributes(attributes: UserAttributes) {
    this._localAttributes = attributes;
  }

  /**
   * Update the Cognito copy of user attributes.
   * Syncs the local attributes with Cognito
   * @returns A promise that resolves when the user attributes are updated
   */
  override updateUserAttributes(): Promise<void> {
    return this.updateUserData();
  }

  /*
   * Fetch the user data from Cognito and return as UserAttributes.
   * Sends a getUserData request to Cognito converting the response
   * to a UserAttributes object.
   * @returns A promise that resolves with a UserAttributes object
   * when the user data is fetched
   */
  fetchUserData(bypassCache = false): Promise<UserAttributes> {
    const cognitoUser = this.cognitoUser;
    if (!cognitoUser) return Promise.reject(new Error('Cognito user is null'));
    return new Promise((resolve, reject) => {
      cognitoUser.getUserData(
        (err?: Error, userData?: AmazonCognitoIdentity.UserData) => {
          if (err) {
            reject(err);
          } else {
            const getAttValue = (attributeName: string, defaultValue: string): string => {
              const attribute = userData?.UserAttributes.find(
                (attribute) => attribute.Name === attributeName,
              );
              return attribute ? attribute.Value : defaultValue;
            };

            const decodeToken = (): any => {
              const token = this.jwtToken;
              if (!token) return null;
              return JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString());
            };

            const decodedToken = decodeToken();

            //console.log('decodedToken', decodedToken);

            const roles =
              decodedToken && decodedToken['roles'] ? decodedToken['roles'].split(',') : [];

            const attrbutes: UserAttributes = {
              id: getAttValue('sub', ''),
              username: getAttValue('email', ''),
              email: getAttValue('email', ''),
              name: getAttValue('given_name', ''),
              surname: getAttValue('family_name', ''),
              phone: getAttValue('phone_number', ''),
              address: getAttValue('address', ''),
              dob: getAttValue('birthdate', ''),
              picture: getAttValue('picture', ''),
              locale: getAttValue('locale', ''),
              zoneInfo: getAttValue('zoneinfo', ''),
              userId: decodedToken && decodedToken['userId'] ? decodedToken['userId'] : '',
              //adminLevel: parseInt(getAttValue('custom:admin_level', '0')),
              adminLevel:
                decodedToken && decodedToken['adminLevel'] ? Number(decodedToken['adminLevel']) : 0,
              tenant_id: decodedToken && decodedToken['tenant_id'] ? decodedToken['tenant_id'] : '', // '67decf31-a6ca-4986-a46c-b439709e128d', //getAttValue('custom:org_id', ''), //Hardcoding for now
              is_internal_user:
                decodedToken && decodedToken['is_internal_user'] === 'true' ? 'true' : 'false',
              emailVerified: getAttValue('email_verified', 'false') === 'true',
              phoneVerified: getAttValue('phone_number_verified', 'false') === 'true',
              roles: roles,
              is_super_admin: roles.includes('Super Admin') || false,
            };

            resolve(attrbutes);
          }
        },
        { bypassCache },
      );
    });
  }

  /**
   * Update the user data in Cognito with the current local attributes
   * Converts the UserAttributes to CognitoUserAttributeData and sends
   * an updateAttributes request to Cognito
   * @returns A promise that resolves when the user data is updated
   */
  updateUserData(): Promise<void> {
    const cognitoUser = this.cognitoUser;
    if (!cognitoUser) return Promise.reject(new Error('Cognito user is null'));
    return new Promise((resolve, reject) => {
      const cognito_atts: AmazonCognitoIdentity.ICognitoUserAttributeData[] = [
        {
          Name: 'given_name',
          Value: this.attributes.name,
        },
        {
          Name: 'family_name',
          Value: this.attributes.surname,
        },
        {
          Name: 'address',
          Value: this.attributes.address,
        },
        {
          Name: 'birthdate',
          Value: this.attributes.dob,
        },
        {
          Name: 'picture',
          Value: this.attributes.picture,
        },
        {
          Name: 'locale',
          Value: this.attributes.locale,
        },
        {
          Name: 'zoneinfo',
          Value: this.attributes.zoneInfo,
        },
      ];

      cognitoUser.updateAttributes(cognito_atts, (err?: Error, result?: string) => {
        if (err) {
          console.log('Error updating attributes ' + err);
          reject(err);
        } else {
          // Bypass the SDK's localStorage cache — we just wrote new attributes
          // and need a fresh read to avoid serving stale data on subsequent calls.
          this.fetchUserData(true).then((attributes) => {
            this.attributes = attributes;
            // for now lets also sync the user account details
            UserAccountAPI.updateDetails(this).then(() => {
              resolve();
            });
          });
        }
      });
    });
  }

  //
  // Session Tokens
  //

  /**
   * Get the id token for the current session
   * @returns The id token string for the current session
   */
  get idToken() {
    return this._session ? this._session.getIdToken().getJwtToken() : null;
  }

  /**
   * Get the access token for the current session
   * @returns The access token string for the current session
   */
  get accessToken() {
    return this._session ? this._session.getAccessToken().getJwtToken() : null;
  }

  /**
   * Get the refresh token for the current session
   * @returns The refresh token string for the current session
   */
  get refreshToken() {
    return this._session ? this._session.getRefreshToken().getToken() : null;
  }

  /**
   * Get the jwt token for the current session, the jwt token is
   * currently the id token. We'll use this method for most auth requests
   * to allow us to switch to a different token if required.
   * @returns The jwt token string for the current session
   */
  get jwtToken() {
    return this._session ? this._session.getIdToken().getJwtToken() : null;
  }

  /**
   * Is the user currently authenticated
   * @returns True if the user is authenticated, otherwise false
   */
  get isAuthenticated() {
    return this._session ? this._session.isValid() : false;
  }

  /**
   * Does the user have a refresh token available for silent re-authentication
   * @returns True if a refresh token exists in the current session
   */
  get hasRefreshToken(): boolean {
    return !!this._session?.getRefreshToken()?.getToken();
  }

  /**
   * Get the current AuthChallengeName. If the authorisation flow
   * is in a challenge state this will return the current challenge.
   * @returns The current AuthChallengeName if any, otherwise NONE
   */
  get authChallengeState(): AuthChallengeName {
    return this._authChallengeName || AuthChallengeName.NONE;
  }
}
